'use client';

import { Form, Formik } from 'formik';
import { toFormikValidationSchema } from 'zod-formik-adapter';
import { Grid, Stack, Typography, Box } from '@mui/material';
import { useTranslate } from '@jd/i18n';
import { generateValidationSchema } from './formBuilder';
import useInitialValues from './prefill/useInitialValues';
import Optional from '../../../Optional';
import CtaButton from '../../../elements/ctaButton';
import FieldRenderer from './FieldRenderer';
import useSubmit from './useSubmit';
import { DynamicFormType } from '../type';
import RecaptchaLinks from '../../../../providers/Recaptcha/RecaptchaLinks';
import FormLoadingOverlay from './loading/FormLoadingOverlay';
import FormConfirmationOverlay from './confirmation/FormConfirmationOverlay';

type Props = {
  data: DynamicFormType;
};

const SingleStepForm = ({ data }: Props) => {
  const { form } = data;
  const common_t = useTranslate('common');
  const { onSubmit, status, isLoading, dismissConfirmation } = useSubmit({
    id: form?.id,
    fields: form?.fields || [],
    withRecaptcha: form?.withRecaptcha,
    redirect: form?.redirect,
    metaTracking: form?.metaTracking,
    googleAdsTracking: form?.googleAdsTracking,
    conditionalRedirects: form?.conditionalRedirects,
  });

  const initialValues = useInitialValues({ fields: form?.fields || [] });
  const validationSchema = toFormikValidationSchema(
    generateValidationSchema(form?.fields || [], common_t),
  );

  return (
    <Formik
      enableReinitialize
      onSubmit={onSubmit}
      initialValues={initialValues}
      validationSchema={validationSchema}
    >
      {({ isSubmitting }) => (
        <Form>
          <FormLoadingOverlay
            active={isLoading}
            messages={form?.loadingMessages}
          >
            <FormConfirmationOverlay
              active={!!status.success}
              message={form?.confirmationMessage}
              onClose={dismissConfirmation}
            >
              <Stack spacing={2} sx={{ color: 'text.primary' }}>
                <Grid container spacing={2}>
                  {form?.fields.map((field) => {
                    const columns = ((field.width || 100) / 100) * 12;
                    return (
                      <Grid
                        size={{
                          xs: 12,
                          md: columns,
                        }}
                        key={field.name}
                      >
                        <FieldRenderer field={field} />
                      </Grid>
                    );
                  })}
                </Grid>

                <Optional condition={!!status.error}>
                  <Typography variant="body2" color="error">
                    {status.error}
                  </Typography>
                </Optional>

                <Box>
                  <CtaButton
                    variant="contained"
                    type="submit"
                    disabled={isSubmitting}
                    loading={isSubmitting}
                    loadingPosition="start"
                  >
                    {form?.submitButtonLabel || common_t('submit')}
                  </CtaButton>
                </Box>
                {form?.withRecaptcha ? <RecaptchaLinks /> : null}
              </Stack>
            </FormConfirmationOverlay>
          </FormLoadingOverlay>
        </Form>
      )}
    </Formik>
  );
};

export default SingleStepForm;
